import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mic, MicOff, Volume2, VolumeX, Loader2, X, HelpCircle, Bot, Sparkles, Send, RotateCcw, Zap, Bell, TrendingUp, TrendingDown, Activity, Brain, Target, Grid3X3, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Contextual quick actions based on current page
const PAGE_SUGGESTIONS = {
  '/dashboard': [
    { label: 'Portfolio summary', prompt: 'Give me a quick portfolio summary' },
    { label: 'Top signals', prompt: 'What are the current AI signals?' },
    { label: 'Market overview', prompt: 'How are the markets doing today?' },
  ],
  '/portfolio': [
    { label: 'Analyze holdings', prompt: 'Analyze my portfolio allocation' },
    { label: 'Best performer', prompt: 'What is my best performing asset?' },
    { label: 'Rebalance tips', prompt: 'Should I rebalance my portfolio?' },
  ],
  '/trading': [
    { label: 'BTC signal', prompt: 'Should I buy Bitcoin right now?' },
    { label: 'ETH price', prompt: 'What is the current Ethereum price?' },
    { label: 'Market sentiment', prompt: 'What is the overall market sentiment?' },
  ],
  '/bots': [
    { label: 'List my bots', prompt: 'List all my trading bots' },
    { label: 'Create a bot', prompt: 'Help me create a new trading bot' },
    { label: 'Bot performance', prompt: 'How are my bots performing?' },
  ],
  '/ai-signals': [
    { label: 'Strongest signal', prompt: 'What is the strongest AI signal right now?' },
    { label: 'BTC prediction', prompt: 'Get prediction for Bitcoin' },
    { label: 'Signal accuracy', prompt: 'How accurate have the AI signals been?' },
  ],
  '/grid-bots': [
    { label: 'Grid strategy tips', prompt: 'What is a good grid bot strategy for the current market?' },
    { label: 'Best pair for grid', prompt: 'Which trading pair works best for grid bots?' },
  ],
  '/backtesting': [
    { label: 'Backtest BTC', prompt: 'Help me set up a backtest for Bitcoin' },
    { label: 'Strategy ideas', prompt: 'Suggest a strategy to backtest' },
  ],
  '/power-bot': [
    { label: 'Scan opportunities', prompt: 'What trading opportunities are available right now?' },
    { label: 'Power bot tips', prompt: 'How should I configure my Power Bot?' },
  ],
  '/charts': [
    { label: 'BTC analysis', prompt: 'Give me a technical analysis of Bitcoin' },
    { label: 'Key levels', prompt: 'What are the key support and resistance levels for BTC?' },
  ],
  '/paper-trading': [
    { label: 'Practice strategy', prompt: 'Suggest a good paper trading strategy for beginners' },
    { label: 'My paper trades', prompt: 'How are my paper trades performing?' },
  ],
  '/ai-strategy': [
    { label: 'Build strategy', prompt: 'Help me build a trading strategy' },
    { label: 'Risk assessment', prompt: 'What risk level should I use?' },
  ],
};

function getSuggestions(pathname) {
  return PAGE_SUGGESTIONS[pathname] || PAGE_SUGGESTIONS['/dashboard'];
}

const SEVERITY_STYLES = {
  hot: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', badge: 'bg-red-500' },
  warning: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', badge: 'bg-amber-500' },
  info: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400', badge: 'bg-blue-500' },
};

const ALERT_ICONS = {
  trending_up: TrendingUp,
  trending_down: TrendingDown,
  activity: Activity,
  brain: Brain,
  zap: Zap,
  grid: Grid3X3,
  target: Target,
};

function AlertCard({ alert, onDismiss, onAction }) {
  const style = SEVERITY_STYLES[alert.severity] || SEVERITY_STYLES.info;
  const IconComponent = ALERT_ICONS[alert.icon] || Activity;

  return (
    <div className={`px-4 py-3 ${style.bg} hover:brightness-110 transition-all`} data-testid={`alert-card-${alert.id}`}>
      <div className="flex items-start gap-2.5">
        <div className={`w-7 h-7 rounded-full ${style.badge} bg-opacity-20 flex items-center justify-center shrink-0 mt-0.5`}>
          <IconComponent className={`w-3.5 h-3.5 ${style.text}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-xs font-semibold ${style.text}`}>{alert.title}</span>
          </div>
          <p className="text-[11px] text-slate-400 mt-0.5 leading-relaxed line-clamp-2">{alert.message}</p>
          <div className="flex items-center gap-2 mt-1.5">
            <button
              onClick={onAction}
              className={`text-[10px] font-medium ${style.text} hover:underline flex items-center gap-0.5`}
              data-testid={`alert-action-${alert.id}`}
            >
              {alert.action.label} <ChevronRight className="w-3 h-3" />
            </button>
            <button
              onClick={onDismiss}
              className="text-[10px] text-slate-600 hover:text-slate-400 transition-colors"
              data-testid={`alert-dismiss-${alert.id}`}
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function GlobalVoiceAssistant({ user, isAuthenticated }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [voiceStatus, setVoiceStatus] = useState(null);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [inputText, setInputText] = useState('');

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const conversationEndRef = useRef(null);

  // Proactive alerts state
  const [proactiveAlerts, setProactiveAlerts] = useState([]);
  const [dismissedAlertIds, setDismissedAlertIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem('bthq_dismissed_alerts') || '[]'); } catch { return []; }
  });
  const [showAlerts, setShowAlerts] = useState(false);

  const unreadAlerts = proactiveAlerts.filter(a => !dismissedAlertIds.includes(a.id));
  const unreadCount = unreadAlerts.length;

  // Poll for proactive alerts every 60s
  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchAlerts = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/proactive-alerts`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) return;
        const data = await res.json();
        if (data.alerts) setProactiveAlerts(data.alerts);
      } catch {}
    };
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 60000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const dismissAlert = (alertId) => {
    const updated = [...dismissedAlertIds, alertId];
    setDismissedAlertIds(updated);
    localStorage.setItem('bthq_dismissed_alerts', JSON.stringify(updated));
  };

  const dismissAllAlerts = () => {
    const allIds = proactiveAlerts.map(a => a.id);
    setDismissedAlertIds(allIds);
    localStorage.setItem('bthq_dismissed_alerts', JSON.stringify(allIds));
    setShowAlerts(false);
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    const checkStatus = async () => {
      try {
        const res = await fetch(`${API_URL}/api/voice/status`);
        const data = await res.json();
        setVoiceStatus(data);
      } catch (err) {
        setVoiceStatus({ available: false });
      }
    };
    checkStatus();
  }, [isAuthenticated]);

  useEffect(() => {
    if (conversationHistory.length > 0) {
      conversationEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversationHistory]);

  // Load conversation history from DB when panel opens
  useEffect(() => {
    if (!isOpen || !isAuthenticated) return;
    const userId = user?.user_id || user?.id || user?.email;
    if (!userId) return;
    const loadHistory = async () => {
      try {
        const res = await fetch(`${API_URL}/api/voice/history?user_id=${encodeURIComponent(userId)}`);
        const data = await res.json();
        if (data.success && data.messages?.length > 0) {
          setConversationHistory(data.messages.map(m => ({
            role: m.role,
            content: m.content,
            timestamp: m.created_at ? new Date(m.created_at) : new Date()
          })));
        }
      } catch (err) {
        // Silently fail — fresh conversation is fine
      }
    };
    loadHistory();
  }, [isOpen, isAuthenticated, user]);

  // Clear conversation from DB
  const clearConversation = async () => {
    setConversationHistory([]);
    const userId = user?.user_id || user?.id || user?.email;
    if (userId) {
      try {
        await fetch(`${API_URL}/api/voice/history?user_id=${encodeURIComponent(userId)}`, { method: 'DELETE' });
      } catch (err) { /* ignore */ }
    }
  };

  const addToConversation = (role, content) => {
    setConversationHistory(prev => [...prev, { role, content, timestamp: new Date() }]);
  };

  // Execute action returned by AI
  const executeAction = useCallback((action) => {
    if (!action || !action.type) return;

    switch (action.type) {
      case 'navigate':
        if (action.params?.path) {
          navigate(action.params.path);
          toast.success(`Navigated to ${action.params.page_name || action.params.path}`);
        }
        break;
      case 'toggle_theme':
        document.documentElement.classList.toggle('dark');
        toast.success('Theme toggled');
        break;
      case 'logout':
        localStorage.removeItem('token');
        navigate('/login');
        toast.info('Logged out');
        break;
      case 'get_portfolio':
        navigate('/portfolio');
        break;
      case 'get_signals':
        navigate('/ai-signals');
        break;
      case 'create_bot':
        toast.success('Bot created!');
        navigate('/bots');
        break;
      case 'modify_bot':
        toast.success('Bot updated!');
        if (window.location.pathname === '/bots') window.location.reload();
        break;
      case 'start_bot':
        toast.success('Bot activated!');
        if (window.location.pathname === '/bots') window.location.reload();
        break;
      case 'stop_bot':
        toast.info('Bot paused.');
        if (window.location.pathname === '/bots') window.location.reload();
        break;
      case 'delete_bot':
        toast.success('Bot deleted.');
        if (window.location.pathname === '/bots') window.location.reload();
        break;
      case 'list_bots':
        navigate('/bots');
        break;
      default:
        break;
    }
  }, [navigate]);

  // Send command to backend AI
  const sendToBackend = async (text) => {
    setIsProcessing(true);
    addToConversation('user', text);

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/voice/command`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          text,
          user_id: user?.user_id || user?.id || user?.email,
          voice: isMuted ? null : 'nova',
          page_context: location.pathname
        })
      });

      const data = await res.json();

      if (data.success) {
        const responseText = data.response || 'Command processed.';
        addToConversation('assistant', responseText);

        if (!isMuted && data.audio_response) {
          playAudio(data.audio_response);
        }

        if (data.action) {
          executeAction(data.action);
        }
      } else {
        addToConversation('assistant', data.error || data.response || "Sorry, I couldn't process that.");
      }
    } catch (err) {
      console.error('Voice command error:', err);
      addToConversation('assistant', "Sorry, I'm having trouble connecting. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        await processAudio(audioBlob);
      };

      mediaRecorderRef.current.start();
      setIsListening(true);
    } catch (err) {
      toast.error('Please enable microphone access');
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsListening(false);
    }
  }, []);

  const processAudio = async (audioBlob) => {
    setIsProcessing(true);
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');

      const transcribeRes = await fetch(`${API_URL}/api/voice/transcribe`, {
        method: 'POST',
        body: formData
      });
      const transcribeData = await transcribeRes.json();

      if (transcribeData.success && transcribeData.text) {
        await sendToBackend(transcribeData.text);
      } else {
        addToConversation('assistant', "Sorry, I couldn't understand that. Please try again.");
      }
    } catch (err) {
      addToConversation('assistant', 'Audio processing failed. Please type your command instead.');
    } finally {
      setIsProcessing(false);
    }
  };

  const playAudio = (base64Audio) => {
    try {
      setIsSpeaking(true);
      if (audioRef.current) {
        audioRef.current.src = `data:audio/mp3;base64,${base64Audio}`;
        audioRef.current.play();
      }
    } catch (err) {
      setIsSpeaking(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputText.trim() && !isProcessing) {
      sendToBackend(inputText.trim());
      setInputText('');
    }
  };

  if (!isAuthenticated) return null;

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full shadow-lg shadow-purple-500/30 flex items-center justify-center hover:scale-110 transition-all z-50 group ${unreadCount > 0 ? 'animate-bounce-gentle' : ''}`}
        data-testid="voice-assistant-button"
        title={unreadCount > 0 ? `${unreadCount} new market alerts` : 'Open AI Assistant'}
      >
        <Bot className="w-6 h-6 text-white" />
        {unreadCount > 0 ? (
          <span className="absolute -top-1.5 -right-1.5 min-w-[22px] h-[22px] bg-red-500 rounded-full flex items-center justify-center text-[10px] font-bold text-white border-2 border-slate-950 px-1" data-testid="alert-badge">
            {unreadCount}
          </span>
        ) : (
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full border-2 border-slate-950 animate-pulse" />
        )}
      </button>
    );
  }

  return (
    <>
      <audio ref={audioRef} onEnded={() => setIsSpeaking(false)} />

      <div
        className="fixed bottom-6 right-6 w-96 max-w-[calc(100vw-3rem)] bg-slate-900 rounded-2xl shadow-2xl shadow-purple-500/20 border border-white/10 z-50 overflow-hidden flex flex-col"
        data-testid="voice-assistant-panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-600/20 to-blue-600/20 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              isSpeaking ? 'bg-emerald-500 animate-pulse' : 'bg-gradient-to-r from-purple-600 to-blue-600'
            }`}>
              <Bot className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-white flex items-center gap-2">
                BitTrade AI
                <Sparkles className="w-3 h-3 text-amber-400" />
              </h3>
              <p className="text-xs text-slate-400">
                {voiceStatus?.available ? 'AI Voice & Text Ready' : 'AI Text Mode'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={clearConversation}
              className="p-2 hover:bg-white/5 rounded-lg transition-colors text-slate-400"
              title="New Chat"
              data-testid="voice-new-chat-button"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
            <button
              onClick={() => setShowHelp(!showHelp)}
              className={`p-2 rounded-lg transition-colors ${showHelp ? 'bg-white/10 text-white' : 'text-slate-400 hover:bg-white/5'}`}
              title="Help"
              data-testid="voice-help-button"
            >
              <HelpCircle className="w-4 h-4" />
            </button>
            <button
              onClick={() => setIsMuted(!isMuted)}
              className="p-2 hover:bg-white/5 rounded-lg transition-colors text-slate-400"
              title={isMuted ? 'Unmute' : 'Mute'}
              data-testid="voice-mute-button"
            >
              {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 hover:bg-white/5 rounded-lg transition-colors text-slate-400"
              title="Close"
              data-testid="voice-close-button"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Help Section */}
        {showHelp && (
          <div className="p-4 bg-slate-800/50 border-b border-white/10">
            <h4 className="text-sm font-medium text-white mb-2">I can help you with anything:</h4>
            <div className="space-y-1 text-xs text-slate-400">
              <p>"Create a trading bot for Bitcoin"</p>
              <p>"What's the price of Ethereum?"</p>
              <p>"Show me my portfolio"</p>
              <p>"Navigate to the backtesting page"</p>
              <p>"Alert me when BTC hits $100k"</p>
              <p>"What's the best trading strategy?"</p>
              <p>"Explain what DCA means"</p>
            </div>
            <p className="text-xs text-purple-400 mt-2 font-medium">Ask me anything - I understand natural language!</p>
          </div>
        )}

        {/* Proactive Alerts Section */}
        {unreadAlerts.length > 0 && (
          <div className="border-b border-white/10" data-testid="proactive-alerts-section">
            <button
              onClick={() => setShowAlerts(!showAlerts)}
              className="w-full flex items-center justify-between px-4 py-2.5 bg-gradient-to-r from-amber-500/10 to-orange-500/10 hover:from-amber-500/15 hover:to-orange-500/15 transition-colors"
              data-testid="alerts-toggle-button"
            >
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-amber-400" />
                <span className="text-xs font-medium text-amber-300">{unreadAlerts.length} Market Alert{unreadAlerts.length > 1 ? 's' : ''}</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => { e.stopPropagation(); dismissAllAlerts(); }}
                  className="text-[10px] text-slate-500 hover:text-slate-300 transition-colors"
                  data-testid="dismiss-all-alerts"
                >
                  Clear all
                </button>
                <ChevronRight className={`w-3.5 h-3.5 text-slate-400 transition-transform ${showAlerts ? 'rotate-90' : ''}`} />
              </div>
            </button>

            {showAlerts && (
              <div className="max-h-48 overflow-y-auto divide-y divide-white/5">
                {unreadAlerts.map((alert) => (
                  <AlertCard
                    key={alert.id}
                    alert={alert}
                    onDismiss={() => dismissAlert(alert.id)}
                    onAction={() => {
                      navigate(alert.action.route);
                      dismissAlert(alert.id);
                      toast.success(`Navigated to ${alert.action.label}`);
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Conversation Area */}
        <div className="p-4 h-64 overflow-y-auto space-y-3" data-testid="voice-conversation-area">
          {conversationHistory.length === 0 && !isProcessing && (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-14 h-14 bg-slate-800 rounded-full flex items-center justify-center mb-3">
                <Bot className="w-7 h-7 text-purple-400" />
              </div>
              <p className="text-slate-300 text-sm font-medium">
                Hi{user?.name ? ` ${user.name.split(' ')[0]}` : ''}! I'm your AI assistant.
              </p>
              <p className="text-slate-500 text-xs mt-1 mb-3">
                Ask me anything or try a suggestion below.
              </p>
              <div className="flex flex-wrap gap-1.5 justify-center" data-testid="ai-quick-actions">
                {getSuggestions(location.pathname).map((s, i) => (
                  <button
                    key={i}
                    onClick={() => { sendToBackend(s.prompt); }}
                    className="px-2.5 py-1 text-xs bg-purple-500/10 border border-purple-500/20 text-purple-300 rounded-full hover:bg-purple-500/20 transition-colors"
                    data-testid={`quick-action-${i}`}
                  >
                    <Zap className="w-3 h-3 inline mr-1" />{s.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {conversationHistory.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                msg.role === 'user'
                  ? 'bg-purple-600 rounded-tr-sm'
                  : 'bg-slate-800 rounded-tl-sm'
              }`}>
                <p className="text-sm text-white whitespace-pre-wrap">{msg.content}</p>
              </div>
            </div>
          ))}

          {isProcessing && (
            <div className="flex justify-start">
              <div className="bg-slate-800 rounded-2xl px-4 py-2">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-purple-400" />
                  <span className="text-sm text-slate-400">Thinking...</span>
                </div>
              </div>
            </div>
          )}

          <div ref={conversationEndRef} />
        </div>

        {/* Input Area */}
        <form onSubmit={handleSubmit} className="p-4 border-t border-white/10 bg-slate-800/30">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder="Ask me anything..."
              className="flex-1 bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-purple-500 transition-colors"
              disabled={isProcessing || isListening}
              data-testid="voice-text-input"
            />

            {/* Send button */}
            {inputText.trim() && (
              <button
                type="submit"
                disabled={isProcessing}
                className="w-11 h-11 rounded-full flex items-center justify-center bg-purple-600 hover:bg-purple-500 transition-colors"
                data-testid="voice-send-button"
              >
                <Send className="w-4 h-4 text-white" />
              </button>
            )}

            {/* Voice Button */}
            <button
              type="button"
              onClick={isListening ? stopRecording : startRecording}
              disabled={isProcessing || !voiceStatus?.available}
              className={`w-11 h-11 rounded-full flex items-center justify-center transition-all shrink-0 ${
                isListening
                  ? 'bg-red-500 animate-pulse'
                  : isProcessing
                  ? 'bg-slate-700 cursor-not-allowed'
                  : voiceStatus?.available
                  ? 'bg-gradient-to-r from-purple-600 to-blue-600 hover:scale-105'
                  : 'bg-slate-700 cursor-not-allowed'
              }`}
              data-testid="voice-mic-button"
              title={voiceStatus?.available ? (isListening ? 'Stop' : 'Speak') : 'Voice unavailable'}
            >
              {isProcessing ? (
                <Loader2 className="w-5 h-5 text-white animate-spin" />
              ) : isListening ? (
                <MicOff className="w-5 h-5 text-white" />
              ) : (
                <Mic className="w-5 h-5 text-white" />
              )}
            </button>
          </div>

          {isListening && (
            <div className="mt-2 flex items-center justify-center gap-2">
              <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
              <span className="text-xs text-red-400">Listening... Tap to stop</span>
            </div>
          )}
        </form>
      </div>
    </>
  );
}
